import { MapContextType } from "../map";
import {
    MapContextDataset,
    MapContextPolygon,
    MapContextPolygonSource,
} from "../types";
import { v4 as uuid } from "uuid";
import isEmpty from "lodash/isEmpty";
import {
    toNonReadonlyArray,
    GeospatialMultipolygon,
    GeospatialSelection,
    GeospatialSelectionType,
    GeospatialViewport,
} from "@biggeo/bg-utils";
import {
    DatabaseType,
    FilterObject,
    FilterType,
    InputPoint,
    InputPolygon,
    InputViewBox,
    LogicOperator,
    WhereOperator,
} from "@biggeo/bg-server-lib/studio";
import { pipe } from "fp-ts/lib/function";
import * as A from "fp-ts/Array";
import {
    FilterItem as ProtoFilterItem,
    RequestOptions,
    LogicOperator as LogicOperatorProto,
    DatabaseType as ProtoDatabaseType,
    FilterType as ProtoFilterType,
    WhereOperator as ProtoWhereOperator,
} from "../../../../velocity/aurora/filter";
import compact from "lodash/compact";
import { SpatialReq } from "../../../../velocity/aurora/spatial";
import { Polygon } from "../../../../velocity/bgsearch/bgsearch_types";
import { match } from "ts-pattern";
import isEqual from "lodash/isEqual";
import filter from "lodash/filter";
import some from "lodash/some";
import { bbox, bboxClip, booleanOverlap, booleanWithin } from "@turf/turf";
import { Bbox } from "@mapbox/mapbox-gl-geocoder";
import { isPolygonFeature } from "../../../utils/converters";

/**
 * Note: Some functions here shouldn't be put elsewhere then imported. Especially other util files.
 * Somehow it causes the tests to fail because of some dependency issue.
 *
 * */

//  ██████╗ ██████╗ ███╗   ██╗██╗   ██╗███████╗██████╗ ████████╗███████╗██████╗ ███████╗
// ██╔════╝██╔═══██╗████╗  ██║██║   ██║██╔════╝██╔══██╗╚══██╔══╝██╔════╝██╔══██╗██╔════╝
// ██║     ██║   ██║██╔██╗ ██║██║   ██║█████╗  ██████╔╝   ██║   █████╗  ██████╔╝███████╗
// ██║     ██║   ██║██║╚██╗██║╚██╗ ██╔╝██╔══╝  ██╔══██╗   ██║   ██╔══╝  ██╔══██╗╚════██║
// ╚██████╗╚██████╔╝██║ ╚████║ ╚████╔╝ ███████╗██║  ██║   ██║   ███████╗██║  ██║███████║
//  ╚═════╝ ╚═════╝ ╚═╝  ╚═══╝  ╚═══╝  ╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝╚═╝  ╚═╝╚══════╝

export const convertOperatorProto = (
    operator: WhereOperator
): ProtoWhereOperator => {
    return match(operator)
        .with(WhereOperator.contains, () => ProtoWhereOperator.CONTAINS)
        .with(WhereOperator.equals, () => ProtoWhereOperator.EQUALS)
        .with(WhereOperator.startsWith, () => ProtoWhereOperator.STARTS_WITH)
        .with(WhereOperator.endsWith, () => ProtoWhereOperator.ENDS_WITH)
        .with(WhereOperator.isEmpty, () => ProtoWhereOperator.IS_EMPTY)
        .with(WhereOperator.isNotEmpty, () => ProtoWhereOperator.IS_NOT_EMPTY)
        .with(WhereOperator.isAnyOf, () => ProtoWhereOperator.IS_ANY_OF)
        .with(WhereOperator.eq, () => ProtoWhereOperator.EQ)
        .with(WhereOperator.neq, () => ProtoWhereOperator.NEQ)
        .with(WhereOperator.lt, () => ProtoWhereOperator.LT)
        .with(WhereOperator.lte, () => ProtoWhereOperator.LTE)
        .with(WhereOperator.gt, () => ProtoWhereOperator.GT)
        .with(WhereOperator.gte, () => ProtoWhereOperator.GTE)
        .with(WhereOperator.none, () => ProtoWhereOperator.NONE)
        .with(WhereOperator.is, () => ProtoWhereOperator.IS)
        .exhaustive();
};

export const convertFilterTypeProto = (
    filterItem: FilterType
): ProtoFilterType => {
    return match(filterItem)
        .with(FilterType.StringType, () => ProtoFilterType.StringType)
        .with(FilterType.NumberType, () => ProtoFilterType.NumberType)
        .with(FilterType.DateType, () => ProtoFilterType.DateType)
        .with(FilterType.BooleanType, () => ProtoFilterType.BooleanType)
        .exhaustive();
};

export const convertLogicOperatorProto = (
    logicOperator: LogicOperator
): LogicOperatorProto => {
    return match(logicOperator)
        .with(LogicOperator.and, () => LogicOperatorProto.AND)
        .with(LogicOperator.or, () => LogicOperatorProto.OR)
        .exhaustive();
};

export const convertDatabaseTypeProto = (
    databaseType: DatabaseType
): ProtoDatabaseType => {
    return match(databaseType)
        .with(DatabaseType.point, () => ProtoDatabaseType.POINT)
        .with(DatabaseType.polygon, () => ProtoDatabaseType.POLYGON)
        .exhaustive();
};

// ██╗  ██╗███████╗██╗     ██████╗ ███████╗██████╗ ███████╗
// ██║  ██║██╔════╝██║     ██╔══██╗██╔════╝██╔══██╗██╔════╝
// ███████║█████╗  ██║     ██████╔╝█████╗  ██████╔╝███████╗
// ██╔══██║██╔══╝  ██║     ██╔═══╝ ██╔══╝  ██╔══██╗╚════██║
// ██║  ██║███████╗███████╗██║     ███████╗██║  ██║███████║
// ╚═╝  ╚═╝╚══════╝╚══════╝╚═╝     ╚══════╝╚═╝  ╚═╝╚══════╝

export const closeInputPointPolygon = (polygon: InputPoint[]) => {
    if (polygon.length === 0) {
        return [];
    }

    const first = polygon[0];
    const last = polygon[polygon.length - 1];
    if (!first || !last) {
        return [];
    }

    if (
        first.latitude === last.latitude &&
        first.longitude === last.longitude
    ) {
        return polygon;
    }

    return [...polygon, first];
};

export const getInputPolygon = (
    feature: GeoJSON.Feature<GeoJSON.Geometry>,
    id?: string
): InputPolygon & { id?: string } => {
    const featureId = feature.id as string;

    const coordinates =
        feature.geometry.type === "Polygon"
            ? feature.geometry.coordinates[0]
            : [];
    const properties = feature.properties;

    const points = isEmpty(coordinates)
        ? []
        : pipe(
              coordinates,
              A.map((coordinate) => ({
                  latitude: coordinate[1],
                  longitude: coordinate[0],
              })),
              A.filterWithIndex((i: number) => i !== coordinates.length - 1)
          );

    return {
        id: id || featureId,
        inners: [],
        outer: {
            points: closeInputPointPolygon(points),
        },
        properties: properties
            ? {
                  name: properties.name || undefined,
                  fill:
                      properties["fill-color"] && properties["fill-opacity"]
                          ? {
                                color: properties["fill-color"],
                                opacity: properties["fill-opacity"],
                            }
                          : undefined,
                  stroke:
                      properties["stroke-color"] && properties["stroke-opacity"]
                          ? {
                                color: properties["stroke-color"] || undefined,
                                opacity:
                                    properties["stroke-opacity"] || undefined,
                            }
                          : undefined,
                  fkAreaId: properties.fkAreaId || undefined,
                  fkSavedAreaId: properties.fkSavedAreaId || undefined,
              }
            : undefined,
    };
};

export const pickGeospatialSelection = (data: {
    viewport: InputViewBox;
    multipolygon?: InputPolygon[];
}): GeospatialSelection => {
    if (!isEmpty(data.multipolygon)) {
        return {
            _tag: GeospatialSelectionType.multipolygon,
            multipolygon: data.multipolygon || [],
        };
    }

    return {
        _tag: GeospatialSelectionType.viewport,
        viewport: data.viewport,
    };
};

export const geospatialSelectionIsViewport = (
    selection: GeospatialSelection
): selection is GeospatialViewport =>
    selection._tag === GeospatialSelectionType.viewport;

export const geospatialSelectionIsMultipolygon = (
    selection: GeospatialSelection
): selection is GeospatialMultipolygon =>
    selection._tag === GeospatialSelectionType.multipolygon;

export const getSpatialReq = (input: {
    geospatialSelection?: GeospatialSelection;
    filters: {
        fkDataSourceId: string;
        fkOrganizationId: string;
        tableId: string;
        filterObject: FilterObject;
    }[];
    limit?: number;
    offset?: number;
    channel: string;
}): SpatialReq => {
    const { geospatialSelection, filters, limit, offset, channel } = input;

    const multipolygonInput =
        geospatialSelection &&
        geospatialSelectionIsMultipolygon(geospatialSelection)
            ? geospatialSelection.multipolygon
            : undefined;

    const viewportInput =
        geospatialSelection &&
        geospatialSelectionIsViewport(geospatialSelection)
            ? geospatialSelection.viewport
            : undefined;

    return {
        withTable: false,
        requestId: uuid(),
        dateTime: Date.now().toString(),
        channel: channel,
        multiFilters: filters.map((filter) => {
            const options: RequestOptions | undefined = filter.filterObject
                .options
                ? {
                      pointMaxGeomsPerCell:
                          filter.filterObject.options.pointMaxGeomsPerCell ||
                          undefined,
                      pointResolutionOffset:
                          filter.filterObject.options.pointResolutionOffset ||
                          undefined,
                      polygonMaxGeomsPerCell:
                          filter.filterObject.options.polygonMaxGeomsPerCell ||
                          undefined,
                      polygonResolutionOffset:
                          filter.filterObject.options.polygonResolutionOffset ||
                          undefined,
                      geometriesChunkSize:
                          filter.filterObject.options.geometriesChunkSize ||
                          undefined,
                      aggregatesChunkSize:
                          filter.filterObject.options.aggregatesChunkSize ||
                          undefined,
                      maxAdditionalRefines:
                          filter.filterObject.options.maxAdditionalRefines ||
                          undefined,
                      levelSets:
                          toNonReadonlyArray(
                              filter.filterObject.options.levelSets
                          ) || [],
                  }
                : undefined;

            const { fkDataSourceId, fkOrganizationId, tableId } = filter;

            return {
                databaseId: fkDataSourceId,
                organizationId: fkOrganizationId,
                idColumn: tableId || "BG_ID",
                filters: filter.filterObject.filters.map(
                    (filterItem): ProtoFilterItem => ({
                        column: filterItem.column,
                        operator: convertOperatorProto(filterItem.operator),
                        type: convertFilterTypeProto(filterItem.type),
                        data: {
                            stringData: filterItem.data.stringData ?? undefined,
                            booleanData:
                                filterItem.data.booleanData ?? undefined,
                            dateData: filterItem.data.dateData ?? undefined,
                            numberData: filterItem.data.numberData ?? undefined,
                        },
                        id: filterItem.id ?? undefined,
                    })
                ),
                options: options,
                logicOperator: convertLogicOperatorProto(
                    filter.filterObject.logicOperator
                ),
                collection: filter.filterObject.collection,
                databaseType: convertDatabaseTypeProto(
                    filter.filterObject.databaseType
                ),
                limitOffset:
                    limit && offset
                        ? {
                              limit,
                              offset,
                          }
                        : undefined,
            };
        }),
        multipolygon: multipolygonInput
            ? {
                  polygons: multipolygonInput.map((inputPolygon) => ({
                      inners: inputPolygon.inners,
                      outer: inputPolygon.outer,
                  })) as Polygon[],
              }
            : undefined,
        viewport:
            viewportInput && !multipolygonInput
                ? {
                      bottomRight: {
                          latitude: viewportInput.latBounds.min,
                          longitude: viewportInput.lngBounds.max,
                      },
                      topLeft: {
                          latitude: viewportInput.latBounds.max,
                          longitude: viewportInput.lngBounds.min,
                      },
                  }
                : undefined,
    };
};

// ███╗   ███╗██╗   ██╗██╗  ████████╗██╗███████╗██╗██╗  ████████╗███████╗██████╗ ███████╗
// ████╗ ████║██║   ██║██║  ╚══██╔══╝██║██╔════╝██║██║  ╚══██╔══╝██╔════╝██╔══██╗██╔════╝
// ██╔████╔██║██║   ██║██║     ██║   ██║█████╗  ██║██║     ██║   █████╗  ██████╔╝███████╗
// ██║╚██╔╝██║██║   ██║██║     ██║   ██║██╔══╝  ██║██║     ██║   ██╔══╝  ██╔══██╗╚════██║
// ██║ ╚═╝ ██║╚██████╔╝███████╗██║   ██║██║     ██║███████╗██║   ███████╗██║  ██║███████║
// ╚═╝     ╚═╝ ╚═════╝ ╚══════╝╚═╝   ╚═╝╚═╝     ╚═╝╚══════╝╚═╝   ╚══════╝╚═╝  ╚═╝╚══════╝

export const getFilterObjectFromDataset = (
    dataset: MapContextDataset
): FilterObject => ({
    collection: dataset.dataSource.collectionName ?? "",
    databaseId: dataset.dataSource.id,
    databaseType: dataset.dataSource.type ?? DatabaseType.point,
    filters:
        dataset.filters && !isEmpty(dataset.filters.filters)
            ? pipe(
                  dataset.filters.filters,
                  A.map((filter) => ({
                      column: filter.column,
                      operator: filter.operator,
                      type: filter.type,
                      data: filter.data,
                  }))
              )
            : [],
    logicOperator: dataset.filters?.logicOperator ?? LogicOperator.and,
    options: dataset.configuration.options,
});

export const getMultiFiltersType = (input: {
    datasets: MapContextDataset[];
}): FilterObject[] => {
    const { datasets } = input;

    const selectedDatasets = pipe(
        datasets,
        A.filter((dataset) => isEqual(dataset.isSelected, true))
    );

    if (!isEmpty(selectedDatasets)) {
        return selectedDatasets.map(getFilterObjectFromDataset);
    }

    return [];
};

/**
 * @description The purpose of this is to update the coordinates of the polygons used for Velocity's fetch.
 * We only fetch for the area of the shape that is visible in the current viewport
 * @example Toggle a dataset > Draw a shape > Zoom in all the way inside that shape, till its lines are no longer visible > Should only see data for the visible part.
 * @returns - Polygons with updated coordinates. Visible part only.
 */
export const getSpatialShapesInputFromContext = (
    i: Pick<MapContextType, "polygons" | "viewport">
): MapContextPolygon[] => {
    const { polygons, viewport } = i;

    if (polygons.length > 0) {
        const viewportBbox: Bbox = [
            viewport.lngBounds.min,
            viewport.latBounds.min,
            viewport.lngBounds.max,
            viewport.latBounds.max,
        ];

        return polygons
            .map((polygon) => {
                const feature = polygon.feature;

                const featureBbox = bbox(feature);

                // Check if feature bbox is fully inside viewport bbox
                const isFullyVisible =
                    featureBbox[0] >= viewportBbox[0] &&
                    featureBbox[1] >= viewportBbox[1] &&
                    featureBbox[2] <= viewportBbox[2] &&
                    featureBbox[3] <= viewportBbox[3];

                // Only proceed if polygon is not fully visible or the geometry is Polygon
                if (isFullyVisible || !isPolygonFeature(feature)) {
                    return polygon;
                }

                const intersection = bboxClip(feature, viewportBbox);

                if (!intersection) return polygon;

                if (intersection.geometry.type === "Polygon") {
                    return {
                        ...polygon,
                        feature: {
                            ...polygon.feature,
                            geometry: {
                                ...polygon.feature.geometry,
                                ...intersection.geometry,
                            },
                        },
                    };
                }

                return polygon;
            })
            .filter((polygon) =>
                polygon.feature.geometry.type === "Polygon"
                    ? polygon.feature.geometry.coordinates.length > 0
                    : true
            );
    }

    return polygons;
};

/**
 * @description The purpose of this is to remove the boundaries that have inner shapes from the polygons used for Velocity's fetch.
 * We only fetch data for the area inside that drawn shape.
 * @example Toggle a dataset > Select a Boundary > Draw a shape inside that Boundary > Should only see data for that drawn shape.
 * @returns - Filtered polygons
 */
export const getSpatialBoundariesInputFromContext = (
    i: Pick<MapContextType, "polygons">
): MapContextPolygon[] => {
    const { polygons } = i;
    const drawnPolygons = polygons.filter(
        (p) => p.source === MapContextPolygonSource.drawnShape
    );

    const boundaries = polygons.filter(
        (p) => p.source === MapContextPolygonSource.boundary
    );

    if (boundaries.length > 0) {
        const boundariesWithInners = filter(
            boundaries.map((p) => p.feature),
            (boundary) =>
                some(
                    drawnPolygons.map((p) => p.feature),
                    (drawnPolygon) =>
                        booleanOverlap(boundary, drawnPolygon) ||
                        booleanWithin(drawnPolygon, boundary)
                )
        );

        return pipe(
            boundaries.filter(
                (p) => !boundariesWithInners.map((c) => c.id).includes(p.id)
            ),
            A.concat(drawnPolygons),
            A.filter((polygon) =>
                polygon.feature.geometry.type === "Polygon"
                    ? polygon.feature.geometry.coordinates.length > 0
                    : true
            )
        );
    }

    return polygons;
};

/**
 * @description A helper function that takes the Context and extracts the SpatialReq input needed for Velocity.
 * @returns - SpatialReq
 */
export const getSpatialInputFromContext = (
    i: { channelId: string } & Pick<
        MapContextType,
        "polygons" | "viewport" | "datasets"
    >
): SpatialReq => {
    const { viewport, datasets, channelId, polygons: _polygons } = i;

    const multiFilters = getMultiFiltersType({ datasets });

    const datasources = compact(
        datasets.map((d) => {
            const filterObject = multiFilters.find(
                (c) => c.databaseId === d.dataSource.id
            );

            if (!filterObject) return;

            return {
                fkDataSourceId: d.dataSource.id,
                fkOrganizationId: d.dataSource.fkOrganizationId,
                tableId: d.dataSource.tableId || "BG_ID",
                filterObject: filterObject,
            };
        })
    );

    // 1. Send request to Velocity with Viewport
    if (_polygons.length === 0) {
        const geospatialSelection = pickGeospatialSelection({
            viewport,
            multipolygon: undefined,
        });

        return getSpatialReq({
            geospatialSelection,
            filters: datasources,
            channel: channelId,
        });
    }

    const polygons = pipe(
        getSpatialShapesInputFromContext({
            viewport,
            polygons: _polygons,
        }),
        (output) => getSpatialBoundariesInputFromContext({ polygons: output })
    );

    // 2. Send request to Velocity with Polygons
    const geospatialSelection = pickGeospatialSelection({
        viewport,
        multipolygon: polygons.map((polygon) => {
            const input = getInputPolygon(polygon.feature);

            return {
                inners: input.inners,
                outer: input.outer,
            };
        }),
    });

    return getSpatialReq({
        geospatialSelection,
        filters: datasources,
        channel: channelId,
    });
};
